import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { ArticleSummary } from '@/types/newsService';
import { withAlpha } from '@/lib/withAlpha';
import { useHaptics } from '@/hooks/useHaptics';

interface Props {
  article: ArticleSummary;
  locale?: string;
}

/**
 * Main feed card — hero image + tag chip + title + description + meta row.
 * Ported from mobile-app/src/components/news/ArticleFeedCard.tsx (analytics
 * removed).
 */
export const ArticleFeedCard = memo(function ArticleFeedCard({
  article,
  locale = 'en',
}: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation('news');
  const haptics = useHaptics();

  const localized = useMemo(() => {
    const match = article.content.find((c) => c.locale === locale);
    return match ?? article.content[0];
  }, [article.content, locale]);

  const onClick = useCallback(() => {
    haptics.selection();
    navigate(`/news/${article.slug}`);
  }, [navigate, article.slug, haptics]);

  const timeAgo = useMemo(() => {
    const diff = Date.now() - new Date(article.publishedAt).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return t('timeAgo.minutes', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('timeAgo.hours', { count: hours });
    const days = Math.floor(hours / 24);
    if (days < 30) return t('timeAgo.days', { count: days });
    const months = Math.floor(days / 30);
    return t('timeAgo.months', { count: months });
  }, [article.publishedAt, t]);

  if (!localized) return null;

  const tagBg = article.primaryTag?.color
    ? withAlpha(article.primaryTag.color, 0.13)
    : undefined;
  const tagFg = article.primaryTag?.color;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={localized.title}
      className="mx-4 mb-3 block w-[calc(100%-2rem)] overflow-hidden rounded-2xl bg-bg-secondary text-left transition-opacity active:opacity-80"
    >
      {article.heroImage && (
        <img
          src={article.heroImage.thumbnailUrl || article.heroImage.url}
          alt={article.heroImage.alt}
          loading="lazy"
          className="h-[200px] w-full object-cover"
        />
      )}

      <div className="p-4">
        {article.primaryTag && (
          <span
            className="mb-2 inline-block self-start rounded-[10px] px-2.5 py-[3px] text-[11px] font-bold uppercase tracking-[0.5px]"
            style={
              tagBg
                ? { backgroundColor: tagBg, color: tagFg }
                : { backgroundColor: 'rgba(0, 231, 223, 0.12)', color: '#00e7df' }
            }
          >
            {article.primaryTag.name}
          </span>
        )}

        <h3 className="mb-1.5 line-clamp-3 text-[18px] font-bold leading-6 text-fg">
          {localized.title}
        </h3>

        {localized.description ? (
          <p className="mb-2.5 line-clamp-3 text-sm leading-5 text-fg-muted">
            {localized.description}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-[#6a6a6a]">{timeAgo}</span>
          {article.engagement.upvotes > 0 && (
            <span className="text-xs font-medium text-[#6a6a6a]">
              ▲ {article.engagement.upvotes}
            </span>
          )}
        </div>
      </div>
    </button>
  );
});
